import { useMemo } from 'react';

import TrackedActionContent, {
  getContentValue,
} from 'Editor/components/TrackedActionCard/TrackedActionBody';
import AnnotationType from 'PDF/RightSidePanel/AnnotationsPanel/AnnotationCard/ViewAnnotationCard/AnnotationType';

import styles from './Snapshot.module.scss';
import { isRichTextEmpty, RichTextLabel } from '_common/lib/components';

type PreviewProps = {
  testId: string;
  elementType?: 'file' | 'document' | 'folder' | 'dopdf' | 'presentation';
} & (
  | {
      type: 'comment' | 'task';
      data?: string | Snapshot;
      subType?: undefined;
    }
  | {
      type: 'suggestion';
      data: {
        content: ApiSchemas['SuggestionActionExtra']['content'];
        type: ApiSchemas['SuggestionActionExtra']['type'];
      };
      subType?: undefined;
    }
  | {
      type: 'annotation';
      subType: PDF.Annotation['subtype'];
      data?: string | Snapshot;
    }
);

const Preview = ({ data, type, subType, testId, elementType }: PreviewProps) => {
  const normalizeSnapshot = (snapshot: Snapshot) => {
    if (!snapshot) return '';

    if (elementType === 'document') {
      if (typeof snapshot === 'string') return snapshot;

      if (typeof snapshot === 'object' && 'content' in snapshot) {
        return snapshot.content;
      }
    }

    return snapshot;
  };

  const content = useMemo(() => {
    const snapshotData = normalizeSnapshot(data as Snapshot);

    if (snapshotData && !isRichTextEmpty(snapshotData)) {
      return (
        <RichTextLabel
          initialContent={snapshotData}
          testId={`${testId}-rich-text-editor`}
          collapse
          className="**:text-left! **:font-[Arial]! **:text-[12px]! **:text-black!"
        />
      );
    } else {
      if (type === 'suggestion') {
        return (
          <TrackedActionContent
            content={getContentValue({ content: data.content, type: data.type })}
            type={data.type}
          />
        );
      }
      if (typeof data === 'string' && data.trim() !== '') {
        return data;
      } else {
        return null;
      }
    }
  }, [data, type]);

  const subTypeContent = useMemo(() => {
    if (type === 'annotation') {
      return <AnnotationType type={subType} notification />;
    }
    return null;
  }, [type, subType]);

  if (!content && !subTypeContent) {
    return null;
  }

  return (
    <div className={styles.root}>
      {subTypeContent && (
        <div style={content ? { padding: '1rem 0 0.5rem 0' } : undefined}>{subTypeContent}</div>
      )}
      {content && <div className={styles.content}>{content}</div>}
    </div>
  );
};
export default Preview;
